import {
  Injectable,
  computed,
  signal,
  Optional,
  Inject,
  InjectionToken,
} from '@angular/core';

import { interval } from 'rxjs';

import { AnomalyDetector } from './anomaly-detector';
import { AnomalyCheckerFactory } from './checker/anomaly-checker.factory';
import {
  MonitoringBlockedStatus,
  MonitoringFailureReason,
} from './monitoring-anomaly.model';
import {
  DEFAULT_MONITORING_CONFIG,
  MonitoringToolsConfig,
} from './monitoring-tools.config';

/**
 * Injection token for monitoring tools configuration
 */
export const MONITORING_TOOLS_CONFIG =
  new InjectionToken<MonitoringToolsConfig>('MonitoringToolsConfig', {
    providedIn: 'root',
    factory: () => DEFAULT_MONITORING_CONFIG,
  });

/**
 * Interface representing the status of a single monitoring system
 */
export interface MonitoringSystemDetail {
  isActive: boolean;
  failureReason?: MonitoringFailureReason;
  scriptLoaded?: boolean;
  lastChecked?: number;
}

/**
 * Interface representing the status of monitoring systems
 */
export interface MonitoringSystemStatus {
  gtm: boolean;
  smartlook: boolean;
  posthog: boolean;
  clarity: boolean;
}

/**
 * Interface representing detailed monitoring information with failure reasons
 */
export interface MonitoringSystemsDetail {
  gtm: MonitoringSystemDetail;
  smartlook: MonitoringSystemDetail;
  posthog: MonitoringSystemDetail;
  clarity: MonitoringSystemDetail;
}

/**
 * Interface representing detailed monitoring information
 */
export interface MonitoringDetectionResult {
  systems: MonitoringSystemStatus;
  systemsDetail: MonitoringSystemsDetail;
  allActive: boolean;
  blockedSystems: MonitoringBlockedStatus;
  hasNetworkConnection: boolean;
  timestamp: number;
}

/**
 * Service to detect if monitoring systems are blocked or unavailable
 *
 * This service monitors the availability of:
 * - Google Tag Manager (GTM)
 * - Smartlook
 * - PostHog
 * - Microsoft Clarity
 *
 * It continuously checks for their presence and reports anomalies when they're blocked.
 */
@Injectable({
  providedIn: 'root',
})
export class MonitoringDetectionService {
  private readonly CHECK_INTERVAL = 5000; // Check every 5 seconds
  private anomalyDetector: AnomalyDetector;
  private monitoringConfig: MonitoringToolsConfig;

  // Signals for monitoring status with detailed information
  private systemsDetailMap = signal<Map<string, MonitoringSystemDetail>>(
    new Map(),
  );
  private networkConnection = signal<boolean>(true);
  private timestamp = signal<number>(Date.now());

  private isMonitoring = false;

  /**
   * Computed signal that provides the current monitoring detection result
   */
  public monitoringStatus = computed<MonitoringDetectionResult>(() => {
    const systemsMap = this.systemsDetailMap();

    // Build systems status and details dynamically from the map
    const systems: MonitoringSystemStatus = {
      gtm: systemsMap.get('gtm')?.isActive ?? false,
      smartlook: systemsMap.get('smartlook')?.isActive ?? false,
      posthog: systemsMap.get('posthog')?.isActive ?? false,
      clarity: systemsMap.get('clarity')?.isActive ?? false,
    };

    const systemsDetail: MonitoringSystemsDetail = {
      gtm: systemsMap.get('gtm') ?? {
        isActive: false,
        failureReason: MonitoringFailureReason.UNKNOWN,
      },
      smartlook: systemsMap.get('smartlook') ?? {
        isActive: false,
        failureReason: MonitoringFailureReason.UNKNOWN,
      },
      posthog: systemsMap.get('posthog') ?? {
        isActive: false,
        failureReason: MonitoringFailureReason.UNKNOWN,
      },
      clarity: systemsMap.get('clarity') ?? {
        isActive: false,
        failureReason: MonitoringFailureReason.UNKNOWN,
      },
    };

    const blockedSystems: MonitoringBlockedStatus = {
      gtmBlocked: !systems.gtm,
      smartlookBlocked: !systems.smartlook,
      posthogBlocked: !systems.posthog,
      clarityBlocked: !systems.clarity,
    };

    return {
      systems,
      systemsDetail,
      allActive:
        systems.gtm && systems.smartlook && systems.posthog && systems.clarity,
      blockedSystems,
      hasNetworkConnection: this.networkConnection(),
      timestamp: this.timestamp(),
    };
  });

  constructor(
    @Optional() @Inject(MONITORING_TOOLS_CONFIG) config?: MonitoringToolsConfig,
  ) {
    this.monitoringConfig = config ?? DEFAULT_MONITORING_CONFIG;
    // Create anomaly detector with configured checkers
    const checkers = AnomalyCheckerFactory.createCheckers(
      this.monitoringConfig,
    );
    this.anomalyDetector = new AnomalyDetector(checkers);
  }

  /**
   * Start monitoring all systems
   */
  public startMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    // Perform initial check
    this.checkAllSystems();

    // Set up periodic checks
    interval(this.CHECK_INTERVAL).subscribe(() => {
      if (this.isMonitoring) {
        this.checkAllSystems();
      }
    });
  }

  /**
   * Stop monitoring all systems
   */
  public stopMonitoring(): void {
    this.isMonitoring = false;
  }

  /**
   * Perform a one-time check of all monitoring systems
   * @returns Current monitoring status
   */
  public checkAllSystems(): MonitoringDetectionResult {
    // Check network connection first
    const hasNetwork = this.checkNetworkConnection();
    this.networkConnection.set(hasNetwork);

    // Use anomaly detector to validate all systems
    const results = this.anomalyDetector.validate();
    this.systemsDetailMap.set(results);
    this.timestamp.set(Date.now());

    // Return computed result
    return this.monitoringStatus();
  }

  /**
   * Check if user has network connection
   */
  private checkNetworkConnection(): boolean {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true; // Assume connected if can't determine
  }

  /**
   * Get current status synchronously
   */
  public getCurrentStatus(): MonitoringDetectionResult {
    return this.monitoringStatus();
  }

  /**
   * Check if all monitoring systems are active
   */
  public areAllSystemsActive(): boolean {
    return this.monitoringStatus().allActive;
  }

  /**
   * Get list of blocked systems
   */
  public getBlockedSystems(): MonitoringBlockedStatus {
    return this.monitoringStatus().blockedSystems;
  }

  /**
   * Updates the monitoring configuration and recreates the anomaly detector
   * @param config New monitoring configuration
   */
  public updateConfig(config: MonitoringToolsConfig): void {
    this.monitoringConfig = { ...this.monitoringConfig, ...config };
    const checkers = AnomalyCheckerFactory.createCheckers(
      this.monitoringConfig,
    );
    this.anomalyDetector = new AnomalyDetector(checkers);
    // Re-check all systems with new configuration
    this.checkAllSystems();
  }

  /**
   * Gets the current monitoring configuration
   * @returns Current monitoring tools configuration
   */
  public getConfig(): MonitoringToolsConfig {
    return { ...this.monitoringConfig };
  }
}
